import { createSelector } from '@reduxjs/toolkit';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { JurisdictionCode } from '../../common/jurisdictions';
import { getLanguageDirection } from '../../i18n/config';
import { Case } from '../../models/case';
import { createFilteredListSelector, createTextSearchFilter } from '../../redux/selectors/listSelectors';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { createCaseThunk, getCasesFromDBThunk } from '../../redux/thunks/CaseThunks';
import { caseSelectByQuery, caseSelectOneObjectByQuery, userSelectOneObjectByQuery } from '../../redux/thunks/DBServiceThunks';
import CreateCaseDialog from '../common/CreateCaseDialog';
import { Loader2 } from 'lucide-react';
import {
  CaseSearchBox,
  CasesEmptyState,
  CasesList,
  CreateCaseButton
} from './cases';

// Memoized selectors
const selectUser = createSelector(
  [(state) => state.DBUserReducer],
  (userReducer) => userSelectOneObjectByQuery(userReducer, {})
);

const selectCases = createSelector(
  [(state) => state.DBCaseReducer],
  (caseReducer) => caseSelectByQuery(caseReducer, {})
);

const selectCurrentCase = createSelector(
  [(state) => state.DBCaseReducer, (state) => state.CaseReducer.currentCaseId],
  (caseReducer, currentCaseId) => caseSelectOneObjectByQuery(caseReducer, { _id: currentCaseId })
);

// Filtered cases selector
const selectFilteredCases = createFilteredListSelector(
  selectCases,
  createTextSearchFilter((caseItem: Case) => caseItem.title)
);

/**
 * CasesView component for displaying cases in the sidebar
 */
const CaseListView: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();

  const user = useAppSelector(selectUser);
  const currentCase = useAppSelector(selectCurrentCase);
  const isLoading = useAppSelector(state => state.DBCaseReducer.itemsLoading);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredCases = useAppSelector(state => selectFilteredCases(state, searchTerm));

  const handleCreateCase = (title: string, jurisdiction: JurisdictionCode) => {
    if (title.trim()) {
      dispatch(createCaseThunk({ title, jurisdiction }));
    }
    setDialogOpen(false);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  useEffect(() => {
    if (user) {
      dispatch(getCasesFromDBThunk());
    }
  }, [dispatch, user]);

  return (
    <div dir={getLanguageDirection(i18n.language)} className="flex flex-col h-full">
      {/* Search Box */}
      <CaseSearchBox
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
      />

      {/* Cases List, Empty State, or Loading State */}
      <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
        {isLoading && (
          <div className="flex flex-col items-center justify-center h-full p-6 animate-fade-in">
            <Loader2 className="w-10 h-10 animate-spin text-gold mb-4" />
            <p className="text-sm font-medium text-muted-foreground text-center">
              {t('components.cases.loadingCases')}
            </p>
          </div>
        )}

        {!isLoading && filteredCases.length > 0 && (
          <CasesList searchTerm={searchTerm} />
        )}

        {!isLoading && filteredCases.length === 0 && (
          <CasesEmptyState
            searchTerm={searchTerm}
            onClearSearch={handleClearSearch}
          />
        )}
      </div>

      {/* Create Case Button */}
      <div className="mt-4">
        <CreateCaseButton onClick={() => setDialogOpen(true)} />
      </div>

      {/* Create Case Dialog */}
      <CreateCaseDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onCreateCase={handleCreateCase}
      />
    </div>
  );
};

export default CaseListView;
